import AppointmentTypePage from '@components/AppointmentTypePage/AppointmentTypePage';
import BookingDetails from '@components/BookingDetails/BookingDetails';
import CalendarPage from '@components/CalendarPage/CalendarPage';
import {
  Box,
  LuxButton,
  LuxModal,
  LuxModalFooter,
  LuxModalHeader,
  LuxProgressBar,
  LuxStepper,
  LuxTypography,
  Stack,
  useTheme,
} from '@gemini/fe-ds-core';
import { FormIds } from '@utils/enums/FormIds.enum';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, Switch, useHistory } from 'react-router-dom';
import { ChooseNewSlot } from '@components/CalendarPage/ChooseNewSlot';
import ReservationTimer from '@components/CalendarPage/ReservationTimer';
import ContactDetailsPage from '@components/ContactDetailsPage/ContactDetailsPage';
import InsurancePage from '@components/InsurancePage/InsurancePage';
import PatientDetailsPage from '@components/PatientDetailsPage/PatientDetailsPage';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { Patient, usePatientStore } from '@store/patientStore';
import { StepsStep } from '@utils/enums/StepsStep.enum';
import {
  BookingTitleDesktop,
  BookingTitleMobile,
  ButtonsWrapper,
  CentralDataContainer,
  ContainerPill,
  ExternalWrapper,
  InnerContainer,
  NavigationWrapper,
  RightContainer,
  StepperWrapper,
} from './Steps.styles';
import RemoveModal from '@components/RemoveModal/RemoveModal';
import CustomPage from '@components/CustomPage/CustomPage';
import { ReschedulingBanner } from '@components/CalendarPage/ReschedulingBanner';
import { MultiplePatientsNavigatorMobile } from '@components/MultiplePatientsNavigator/MultiplePatientsNavigatorMobile';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { useEvent } from '@gemini/fe-utils';
import { getPropertiesOfStep } from './Steps.utils';
// import useGetTextColor from '@hooks/useGetTextColor';
import {
  IAutoContinueCheckEvent,
  IChangeEvent,
  ICheckEvent,
  ISaveEvent,
  OBEvents,
} from '@utils/events/events';
import { Helmet } from 'react-helmet';
import { OnlineBookingToast } from '@common/OnlineBookingToast';
import { useUrlsContext } from '@utils/contexts/UrlsContext';
import RouteContainer from './RouteContainer';
import { conditionallyApplyUpperCase } from '@utils/functions';

export interface CurrentData {
  type: FormIds;
  data: any;
}

export interface StepsProps {
  hideHeader: boolean;
  collapsableMobileBookingDetails?: boolean;
}

const Steps = ({
  hideHeader = false,
  collapsableMobileBookingDetails = false,
}: StepsProps) => {
  const { t } = useTranslation();
  const { Urls } = useUrlsContext();
  const theme = useTheme();
  const { emit, subscribe } = useEvent();
  const history = useHistory();
  // const initialStep = useParams<{ id: string }>()?.id;
  // const { getTextColor } = useGetTextColor();
  const currentStep = useOnlineBookingStore(state => state.currentFormStep);
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const isRescheduling = useOnlineBookingStore(state => state.isRescheduling);
  const [toastType, setToastType] = useState<string>('');
  const [showToastMsg, setShowToastMsg] = useState<string>('');
  const [patientsToCheck, setPatientsToCheck] = useState<string[]>([]);
  const brandName = useOnlineBookingStore(state => state.brandName);

  const setFormStep = useOnlineBookingStore(state => state.setFormStep);
  const showDeletePatientModal = usePatientStore(
    state => state.showDeletePatientModal,
  );
  const setShowDeletePatientModal = usePatientStore(
    state => state.setShowDeletePatientModal,
  );
  const setIsNewPatientStarted = useOnlineBookingStore(
    state => state.setIsNewPatientStarted,
  );
  // boolean to display or hide the continue button
  const showContinue = useOnlineBookingStore(state => {
    // SCOMMENTARE PER ABILITARE LA LOGICA PER MOSTRARE/NASCONDERE IL CONTINUE CTA
    const showContinueFromConfig =
      state.generalConfig?.practiceInformation?.showContinue ?? true;

    // Continue CTA Whitelist based on the current step
    // if showContinueFromConfig is false, follow the whitelist
    const continueCTAWhitelist: Record<StepsStep, boolean> = {
      [StepsStep.APPOINTMENT_TYPE]: true,
      [StepsStep.INSURANCE]: true,
      [StepsStep.SCHEDULE_EXAM]: true,
      [StepsStep.PATIENT_DETAILS]: true,
      [StepsStep.CUSTOM_PAGE]: true,
      [StepsStep.CONTACT_DETAILS]: true,
      [StepsStep.TYP]: false,
    };

    return showContinueFromConfig
      ? true
      : (continueCTAWhitelist[currentStep] ?? true);
  });

  const [shouldAutoContinue, setShouldAutoContinue] = useState(false);

  const [openGoToPracticeCardModal, setOpenGoToPracticeCardModal] =
    useState(false);

  const stepperAlignment = useOnlineBookingStore(
    state =>
      state.generalConfig?.practiceInformation?.stepperAlignment || 'right',
  );

  const patients = usePatientStore(state => state.patients);
  const currentPatientIndex = useOnlineBookingStore(
    state => state.currentPatient,
  );
  const isStartedNewPatient = useOnlineBookingStore(
    state => state.isStartedNewPatient,
  );
  const showAddPatientFrame = useOnlineBookingStore(
    state => state.showAddPatientFrame,
  );
  const setShowAddPatientFrame = useOnlineBookingStore(
    state => state.setShowAddPatientFrame,
  );

  const deletePatient = usePatientStore(state => state.deletePatient);

  const [enableButton, setEnableButton] = useState(false);
  const [breakpoint, setBreakpoint] = useState<'mobile' | 'desktop'>(
    window.innerWidth > MobileBreakpoint ? 'desktop' : 'mobile',
  );
  const currentPatient: Patient | undefined = useMemo(() => {
    return patients && patients[currentPatientIndex];
  }, [patients, currentPatientIndex]);
  const timer = useMemo(
    () => patients.some(patient => patient?.extraInfoScheduleExam?.timer),
    [patients],
  );
  const chooseNewSlot = useMemo(
    () => currentPatient?.extraInfoScheduleExam?.chooseNewSlot?.type,
    [currentPatient],
  );

  const hasCustomPage = useMemo(() => {
    return formConfigs?.customPage?.show;
  }, [formConfigs]);

  const showPatientInsurance = useMemo(() => {
    return formConfigs?.showPatientInsurance;
  }, [formConfigs]);

  const isReschedulingInScheduleExam = useMemo(
    () => isRescheduling && currentStep === StepsStep.SCHEDULE_EXAM,
    [isRescheduling, currentStep],
  );

  useEffect(() => {
    if (enableButton && shouldAutoContinue) {
      handleContinue();
      setShouldAutoContinue(false); // trigger reset
    }
  }, [enableButton, shouldAutoContinue]);

  const allSteps = useMemo(() => {
    const steps = [
      {
        step: StepsStep.APPOINTMENT_TYPE,
        label: t(`steps.${StepsStep.APPOINTMENT_TYPE}`),
        show: true,
      },
      {
        step: StepsStep.INSURANCE,
        label: t(`steps.${StepsStep.INSURANCE}`),
        show: showPatientInsurance,
      },
      {
        step: StepsStep.SCHEDULE_EXAM,
        label: t(`steps.${StepsStep.SCHEDULE_EXAM}`),
        show: true,
      },
      {
        step: StepsStep.PATIENT_DETAILS,
        label: t(`steps.${StepsStep.PATIENT_DETAILS}`),
        show: true,
      },
      {
        step: StepsStep.CUSTOM_PAGE,
        label:
          formConfigs?.customPage?.name || t(`steps.${StepsStep.CUSTOM_PAGE}`),
        show: hasCustomPage,
      },
      {
        step: StepsStep.CONTACT_DETAILS,
        label: t(`steps.${StepsStep.CONTACT_DETAILS}`),
        show: true,
      },
    ];
    return steps;
  }, [t, showPatientInsurance, hasCustomPage]);

  const formSteps = useMemo(() => {
    const labels: string[] = [];
    allSteps.forEach(step => {
      if (step.show) {
        labels.push(step.label as string);
      }
    });
    return labels;
  }, [allSteps]);

  const valueProgressBar = useMemo(() => {
    const onlyShow = allSteps.filter(step => step.show);
    const perc = 100 / onlyShow.length;
    const index = onlyShow.findIndex(step => step.step === currentStep);
    return index * perc;
  }, [currentStep, allSteps]);

  // useEffect(() => setStep(Number(initialStep)), [initialStep, setStep]);

  const handleNextStep = () => {
    // TODO Spostare e usare un MEMO

    const propPage = getPropertiesOfStep(
      currentStep,
      Urls,
      showPatientInsurance,
      hasCustomPage,
      isRescheduling,
    );
    setEnableButton(false);
    /**
     * Handle navigation in two ways: by emitting events to notify the underlying component to save data and move to the next page.
     * The default behavior is to leave things as they are.
     * This approach ensures that the next page has access to the updated data.
     */
    switch (currentStep) {
      case StepsStep.APPOINTMENT_TYPE:
        emit<ISaveEvent>(OBEvents.apptSave, {
          currentStep,
          propPage,
        });
        break;
      case StepsStep.INSURANCE:
        emit<ISaveEvent>(OBEvents.insSave, {
          currentStep,
          propPage,
        });
        break;
      default:
        history.push(`${propPage.next.url}`);
        setFormStep(propPage.next.step);
    }
  };

  const handlePreviousStep = () => {
    // TODO Spostare e usare un MEMO

    const propPage = getPropertiesOfStep(
      currentStep,
      Urls,
      showPatientInsurance,
      hasCustomPage,
      isRescheduling,
    );
    if (propPage && propPage.prev) {
      history.push(`${propPage.prev.url}`);
      setFormStep(propPage.prev.step);
    }
  };

  const handleContinue = useCallback(() => {
    if (
      (!hasCustomPage && currentStep === StepsStep.PATIENT_DETAILS) ||
      (hasCustomPage && currentStep === StepsStep.CUSTOM_PAGE)
    ) {
      const onlyTruePatient = patients.filter(p => p);
      if (!showAddPatientFrame && onlyTruePatient.length < 3) {
        setShowAddPatientFrame(true);
      } else {
        handleNextStep();
      }
    } else {
      handleNextStep();
    }
    setToastType('');
    setPatientsToCheck([]);
  }, [currentStep, hasCustomPage, showAddPatientFrame, patients]);

  const handleBack = () => {
    //if first step, open modal to go to practice card section
    if (currentStep === StepsStep.APPOINTMENT_TYPE) {
      setOpenGoToPracticeCardModal(true);
    }
    if (
      (currentStep === StepsStep.CUSTOM_PAGE ||
        currentStep === StepsStep.PATIENT_DETAILS) &&
      showAddPatientFrame
    ) {
      setShowAddPatientFrame(false);
    } else {
      handlePreviousStep();
    }
  };

  const handleGoToPracticeCard = () => {
    setOpenGoToPracticeCardModal(false);
    //setto il timeout per aspettare un momento per la chiusura modale poichè altrimenti rimarrebbe overflow hidden -> cancellare il timeout in futuro quando la issue è risolta
    setTimeout(() => history.push(Urls.PRACTICE_CARD), 0);
  };

  const handleDeletePatient = useCallback(() => {
    deletePatient(currentPatientIndex);
  }, [currentPatientIndex]);

  window.addEventListener('resize', () => {
    if (breakpoint === 'mobile' && window.innerWidth > MobileBreakpoint)
      setBreakpoint('desktop');
    if (breakpoint === 'desktop' && window.innerWidth <= MobileBreakpoint)
      setBreakpoint('mobile');
  });

  useEffect(() => {
    if (isStartedNewPatient) {
      history.push(Urls.APPOINTMENT_TYPE);
      setFormStep(StepsStep.APPOINTMENT_TYPE);
      setIsNewPatientStarted(false);
    }
  }, [isStartedNewPatient]);

  useEffect(() => {
    document.body.scrollTop = document.documentElement.scrollTop = 0;
  }, [currentStep, showAddPatientFrame]);

  //LAYOUT EFFECT -> EVERY STEP CHANGE, SET THE FOCUS ON THE TITLE OF SURVEYSTEPCONTENT
  const focusRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    focusRef.current?.focus();
  }, [currentStep]);

  const getActiveStepByCurrentStep = useMemo(() => {
    if (
      currentStep === StepsStep.CONTACT_DETAILS &&
      (!showPatientInsurance || !hasCustomPage)
    ) {
      return currentStep - (!showPatientInsurance && !hasCustomPage ? 2 : 1);
    }
    if (!showPatientInsurance && currentStep > StepsStep.INSURANCE) {
      return currentStep - 1;
    }

    return currentStep;
  }, [currentStep]);

  useEffect(() => {
    const subscribeChange = subscribe<IChangeEvent>(
      OBEvents.apptChange,
      payload => {
        console.log('## Appt/Insur Change - Event SUBSCRIBE ##');
        setToastType(payload.type);
      },
      {
        self: true,
      },
    );

    const subscribeShouldAutoContinue = subscribe<IAutoContinueCheckEvent>(
      OBEvents.shouldAutoContinue,
      payload => {
        //in case of appointment change:
        setShouldAutoContinue(payload?.shouldAutoContinue);
      },
      {
        self: true,
        replay: true,
      },
    );

    return () => {
      subscribeChange();
      subscribeShouldAutoContinue();
    };
  }, []);

  useEffect(() => {
    const subscribeCheck = subscribe<ICheckEvent>(
      OBEvents.schedCheck,
      ({ patients }) => {
        console.log('## Multipatien Schedule Check - Event SUBSCRIBE ##');
        setPatientsToCheck(patients);
      },
      {
        self: true,
      },
    );

    return () => subscribeCheck();
  }, []);

  const renderToastChangeMessage = useMemo(() => {
    if (showToastMsg !== '') {
      return (
        <OnlineBookingToast
          open
          severity="warning"
          onClose={() => {
            setToastType('');
            setShowToastMsg('');
          }}
          title={
            toastType === 'appointment'
              ? t('appointmentTypePage.changingAppErrorFooter')
              : t('insurancePage.changingInsuranceErrorFooter')
          }
          icon="WarningIconCircle"
        />
      );
    } else return <></>;
  }, [showToastMsg, toastType, t]);

  const renderToastCheckMessage = useMemo(() => {
    if (patientsToCheck.length > 0)
      return (
        <OnlineBookingToast
          open
          onClose={() => {}}
          title={t(
            'calendarPage.checkingMultipatientsScheduleError.primaryMessage',
          )}
          description={
            t(
              'calendarPage.checkingMultipatientsScheduleError.secondaryMessage',
            ) + patientsToCheck
          }
          showCloseIcon={false}
        />
      );
    else return <></>;
  }, [patientsToCheck, t]);

  useEffect(() => {
    if (toastType !== '') setShowToastMsg(toastType);
    else setShowToastMsg('');
  }, [toastType]);

  const showBackButton = useMemo(
    //For now, hide backbutton in first step for RONA since practice card is not available there
    () => (brandName ? currentStep > 0 : true) && !isReschedulingInScheduleExam,
    [currentStep, isReschedulingInScheduleExam],
  );

  const continueButtonWidth = useMemo(
    () =>
      breakpoint === 'mobile' ? (showBackButton ? 'unset' : '100%') : 'unset',
    [breakpoint, showBackButton],
  );

  return (
    <>
      <Helmet>
        <title>{t('wcag.ob_contact_details_title')}</title>
      </Helmet>
      <RemoveModal
        open={showDeletePatientModal}
        onClose={() => setShowDeletePatientModal(false)}
        yesAction={() => handleDeletePatient()}
        title="modalLabels.removePatientTitle"
        subtitle="modalLabels.removePatientSubtitle"
      />
      <NavigationWrapper hideHeader={hideHeader}>
        <StepperWrapper stepperAlignment={stepperAlignment}>
          {stepperAlignment === 'right' && (
            <>
              <BookingTitleDesktop variant="heading2">
                {t('bookingTitle')}
              </BookingTitleDesktop>
              <BookingTitleMobile variant="heading3">
                {t('bookingTitle')}
              </BookingTitleMobile>
            </>
          )}
          <LuxStepper
            steps={formSteps}
            activeStep={getActiveStepByCurrentStep}
            showOthersCollapsedStepsMobile={!!brandName}
            activeStepInBold={!!brandName}
          />
        </StepperWrapper>
        <LuxProgressBar percentualProgress={valueProgressBar} />
      </NavigationWrapper>
      <ExternalWrapper
        id="scrollable-container"
        backgroundColor={
          brandName && theme.palette.LUX.obTokens.bgColorBaseSecondary
        }
      >
        <CentralDataContainer>
          <InnerContainer role="application">
            <Switch>
              <Route path={Urls.APPOINTMENT_TYPE}>
                <Box>
                  <AppointmentTypePage
                    setEnableButton={setEnableButton}
                    focusRef={focusRef}
                  />
                </Box>
              </Route>
              {showPatientInsurance && (
                <Route path={Urls.INSURANCE}>
                  <RouteContainer focusRef={focusRef}>
                    <InsurancePage setEnableButton={setEnableButton} />
                  </RouteContainer>
                </Route>
              )}
              <Route path={Urls.SCHEDULE_EXAM}>
                <RouteContainer
                  focusRef={focusRef}
                  noStyle
                  showTitle={false}
                  showMultiplePatientsTabs={false}
                >
                  <CalendarPage setEnableButton={setEnableButton} />
                </RouteContainer>
              </Route>
              <Route path={Urls.PATIENT_DETAILS}>
                <RouteContainer focusRef={focusRef}>
                  <PatientDetailsPage setEnableButton={setEnableButton} />
                </RouteContainer>
              </Route>
              {hasCustomPage && (
                <Route path={Urls.CUSTOM_PAGE}>
                  <RouteContainer focusRef={focusRef}>
                    <CustomPage setEnableButton={setEnableButton} />
                  </RouteContainer>
                </Route>
              )}
              <Route path={Urls.CONTACT_DETAILS}>
                <RouteContainer focusRef={focusRef}>
                  <ContactDetailsPage setEnableButton={setEnableButton} />
                </RouteContainer>
              </Route>
              <Route>
                <Redirect to={Urls.APPOINTMENT_TYPE} />
              </Route>
            </Switch>
            <RightContainer manageOrRescheduleMode={hideHeader}>
              <BookingDetails
                accordionModeInMobile={collapsableMobileBookingDetails}
              />
            </RightContainer>
          </InnerContainer>
        </CentralDataContainer>
      </ExternalWrapper>
      <ButtonsWrapper
        step={isReschedulingInScheduleExam ? 0 : currentStep}
        flexDirection="column"
      >
        {/* ##### TOASTS ##### */}
        {chooseNewSlot && currentStep === StepsStep.SCHEDULE_EXAM && (
          <ChooseNewSlot />
        )}
        {isRescheduling && (
          <ContainerPill chooseNewSlot={isRescheduling}>
            {isRescheduling && currentStep === StepsStep.SCHEDULE_EXAM && (
              <ReschedulingBanner />
            )}
          </ContainerPill>
        )}
        {currentStep !== StepsStep.CONTACT_DETAILS && (
          <MultiplePatientsNavigatorMobile />
        )}
        {currentStep === StepsStep.SCHEDULE_EXAM
          ? renderToastCheckMessage
          : renderToastChangeMessage}
        {/* ##### END TOASTS ##### */}
        {breakpoint === 'mobile' && timer && <ReservationTimer />}

        <Stack
          direction="row"
          justifyContent={showBackButton ? 'space-between' : 'flex-end'}
          width="100%"
        >
          {showBackButton && (
            <LuxButton
              variant="ghost"
              // iconStart="ChevronLeftIcon"
              onClick={handleBack}
              style={{
                width: breakpoint === 'mobile' ? '20%' : 'unset',
              }}
            >
              <LuxTypography
                variant="textMedium"
                fontWeight={brandName && '700!important'}
              >
                {t('generalCta.back')}
              </LuxTypography>
            </LuxButton>
          )}
          {showContinue &&
            (breakpoint === 'desktop' ? (
              <div
                style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}
              >
                {timer && <ReservationTimer />}
                <LuxButton
                  onClick={handleContinue}
                  disabled={!enableButton}
                  style={{
                    width: continueButtonWidth,
                  }}
                >
                  <LuxTypography
                    variant={'textMdRegular'}
                    fontWeight={brandName && '700!important'}
                  >
                    {currentStep >= 5
                      ? conditionallyApplyUpperCase(
                          t('generalCta.confirm'),
                          brandName,
                        )
                      : conditionallyApplyUpperCase(
                          t('generalCta.continue'),
                          brandName,
                        )}
                  </LuxTypography>
                </LuxButton>
              </div>
            ) : (
              <LuxButton
                onClick={handleContinue}
                disabled={!enableButton}
                style={{
                  width: continueButtonWidth,
                }}
              >
                <LuxTypography
                  variant={'textMdRegular'}
                  fontWeight={brandName && '700!important'}
                >
                  {currentStep >= 5
                    ? conditionallyApplyUpperCase(
                        t('generalCta.confirm'),
                        brandName,
                      )
                    : conditionallyApplyUpperCase(
                        t('generalCta.continue'),
                        brandName,
                      )}
                </LuxTypography>
              </LuxButton>
            ))}
        </Stack>
      </ButtonsWrapper>
      <LuxModal
        open={openGoToPracticeCardModal}
        size={'small'}
        onClose={() => setOpenGoToPracticeCardModal(false)}
        hideFooterShadow
        dividerFooter
        sx={{
          '& .lux-modal-content-wrapper': {
            padding: '0 1.5rem',
          },
        }}
        header={
          <LuxModalHeader
            darkMode={false}
            enableCloseIcon
            onClose={() => setOpenGoToPracticeCardModal(false)}
            title={
              <LuxTypography variant="heading3">
                {t('modalLabels.goBackTitle')}
              </LuxTypography>
            }
          />
        }
        footer={
          <LuxModalFooter
            justifyContent="space-between"
            onSecondaryButtonClick={() => setOpenGoToPracticeCardModal(false)}
            onPrimaryButtonClick={handleGoToPracticeCard}
            primaryButtonText={conditionallyApplyUpperCase(
              t('modalLabels.yesGoBack'),
              brandName,
            )}
            secondaryButtonText={conditionallyApplyUpperCase(
              t('modalLabels.cancel'),
              brandName,
            )}
            primaryButtonSx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
            secondaryButtonSx={{
              width: {
                xs: '100%',
                sm: 'auto',
              },
            }}
          />
        }
      >
        <LuxTypography variant={'textXs'} fontSize={'0.875rem'}>
          {t('modalLabels.goBackSubtitle')}
        </LuxTypography>
      </LuxModal>
    </>
  );
};

export default Steps;
